import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import CalendarView from './CalendarView';
import HistoryView from './HistoryView';
import NotesSection from './NotesSection';
import amazonAPI from '../services/amazonAPI';

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  green800:  '#1a4a1c',
  green700:  '#1e5e20',
  green600:  '#2a7a2d',
  green500:  '#3a9a3e',
  green200:  '#b8e0bb',
  green100:  '#dff0e0',
  green50:   '#f0f9f1',
  teal:      '#1ec98a',   // ".Smart" brand accent — used for text only
  amber:     '#c8761a',
  amberLight:'#fef3e2',
  bg:        '#f5f8f5',
  white:     '#ffffff',
  text:      '#1a2e1b',
  textMed:   '#4a6a4c',
  textLight: '#8aaa8c',
  border:    '#e0ece0',
  shadow:    'rgba(30,80,32,0.10)',
};

// Priority: critical > high > medium > low
const PRIORITY_STYLE = {
  critical: { bg: '#fdecea', color: '#c0392b', label: 'Critical' },
  high:     { bg: '#fff8e6', color: '#c8761a', label: 'High' },
  medium:   { bg: '#f0f9f1', color: '#1e5e20', label: 'Medium' },
  low:      { bg: '#f5f5f5', color: '#8aaa8c', label: 'Low' },
};

// Sort weight for priority (lower = more urgent)
const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Shared sub-components ──────────────────────────────────────────────────────

function PriorityPill({ priority }) {
  const s = PRIORITY_STYLE[priority] || PRIORITY_STYLE.medium;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      padding: '2px 7px', borderRadius: 99,
      background: s.bg, color: s.color,
      textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {s.label}
    </span>
  );
}

function Checkbox({ checked, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: 22, height: 22, borderRadius: 6,
        border: checked ? 'none' : `2px solid ${T.border}`,
        background: checked ? T.green700 : T.white,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
      }}
    >
      {checked && (
        <svg width="12" height="10" viewBox="0 0 12 10">
          <polyline points="1,5 4,9 11,1" stroke="white" strokeWidth="2"
            fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}

// ── Nav icons ──────────────────────────────────────────────────────────────────
const HomeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M3 12L12 3l9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M5 10v9a1 1 0 001 1h4v-4h4v4h4a1 1 0 001-1v-9"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const CalendarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const ShopIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M3 6h18M16 10a4 4 0 01-8 0"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const SettingsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Category → emoji for product carousel tiles
const CAT_EMOJI = {
  grass_seed:     '🌱',
  fertilizer:     '💚',
  soil_testing:   '🧪',
  tools:          '🔧',
  pest_control:   '🐛',
  weed_control:   '🌿',
  disease_control:'🍄',
  watering:       '💧',
};

// ── ProductCarousel ────────────────────────────────────────────────────────────

function ProductCarousel({ products, monthName, onStoreClick }) {
  const [activeDot, setActiveDot] = useState(0);
  const carouselRef = useRef(null);

  const handleScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = 158; // 150px card + 8px gap
    setActiveDot(Math.round(el.scrollLeft / cardWidth));
  };

  if (!products || products.length === 0) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
            Helpful for {monthName}
          </span>
          <span style={{ fontSize: 11, color: T.textLight, marginLeft: 6 }}>
            — matched to your tasks
          </span>
        </div>
        <button
          onClick={onStoreClick}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 11, color: T.green700, fontWeight: 600, padding: 0,
          }}
        >
          See all ›
        </button>
      </div>

      {/* Scrollable card strip */}
      <div
        ref={carouselRef}
        onScroll={handleScroll}
        style={{
          display: 'flex', gap: 8, overflowX: 'auto',
          paddingBottom: 4,
          scrollbarWidth: 'none', msOverflowStyle: 'none',
          scrollSnapType: 'x mandatory',
        }}
      >
        {products.map(product => (
          <a
            key={product.asin}
            href={product.affiliateUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flexShrink: 0, width: 150,
              background: T.white, borderRadius: 14,
              border: `1px solid ${T.border}`,
              boxShadow: `0 1px 4px ${T.shadow}`,
              padding: '12px 10px 10px',
              display: 'flex', flexDirection: 'column', gap: 6,
              textDecoration: 'none', scrollSnapAlign: 'start',
            }}
          >
            {/* Emoji icon tile */}
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: T.green50, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: 22, flexShrink: 0,
            }}>
              {CAT_EMOJI[product.category] || '🌿'}
            </div>

            {/* Product name */}
            <div style={{
              fontSize: 12, fontWeight: 600, color: T.text,
              lineHeight: 1.3,
              overflow: 'hidden', display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              flex: 1,
            }}>
              {product.title}
            </div>

            {/* Price + rating */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{product.price}</span>
              <span style={{ fontSize: 11, color: T.textMed }}>⭐ {product.rating}</span>
            </div>

            {/* CTA button */}
            <div style={{
              background: T.green700, color: T.white,
              borderRadius: 8, padding: '6px 0',
              fontSize: 10, fontWeight: 600, textAlign: 'center',
            }}>
              Amazon ↗
            </div>
          </a>
        ))}
      </div>

      {/* Dot indicators */}
      {products.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 8 }}>
          {products.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === activeDot ? 16 : 6,
                height: 6, borderRadius: 99,
                background: i === activeDot ? T.green700 : T.border,
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── FocusMode ──────────────────────────────────────────────────────────────────

const FocusMode = ({
  monthName,
  activeYear,
  monthData,
  completedTasks,
  onTaskToggle,
  monthStats,
  onMonthChange,
  onHomeClick,
  onSettingsClick,
  onStoreClick,
  onDismissAlert,
  zoneAlerts = [],
  zoneCustomizedData = {},
}) => {
  const { settings } = useSettings();
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [activeView, setActiveView] = useState('home');
  const [crossingOff, setCrossingOff] = useState(new Set());

  // ── Nav auto-hide on scroll ──────────────────────────────────────────────
  const [navVisible, setNavVisible] = useState(true);
  const scrollRef   = useRef(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const THRESHOLD = 7; // px — responsive to deliberate scroll, ignores micro-jitter

    const onScroll = () => {
      const currentY = el.scrollTop;
      const delta    = currentY - lastScrollY.current;

      // Always show when near the top
      if (currentY < 50) {
        setNavVisible(true);
        lastScrollY.current = currentY;
        return;
      }

      if (delta > THRESHOLD) {
        // Definitively scrolling down
        setNavVisible(false);
        lastScrollY.current = currentY;
      } else if (delta < -THRESHOLD) {
        // Definitively scrolling up
        setNavVisible(true);
        lastScrollY.current = currentY;
      }
      // Between -10 and +10: ignore to avoid jitter
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const monthIndex = MONTHS_FULL.indexOf(monthName);

  // Greeting based on time of day
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? 'Good morning 🌱' :
    hour < 17 ? 'Good afternoon 🌿' :
                'Good evening 🌙';

  // Season label
  const season = monthData?.season
    ? monthData.season.charAt(0).toUpperCase() + monthData.season.slice(1)
    : '';
  const seasonEmoji = { spring: '🌷', summer: '☀️', fall: '🍂', winter: '❄️' }[monthData?.season] || '🌿';

  // Flatten sections → task list, preserving existing ID format
  const flatTasks = useMemo(() => {
    if (!monthData) return [];
    return monthData.sections.flatMap((section, sIdx) =>
      section.tasks.map((task, tIdx) => {
        const id = `${activeYear}-${monthName}-${sIdx}-${tIdx}`;
        return {
          id,
          title: task.text,
          cat: section.title,
          priority: task.priority || 'medium',
          done: completedTasks.has(id),
        };
      })
    );
  }, [monthData, activeYear, monthName, completedTasks]);

  // zoneAlerts are already pre-filtered for dismissal by App.js (lazy-initialized from
  // localStorage before first render). Here we only apply the task-completion auto-hide.
  const activeAlerts = useMemo(() => {
    const doneTitles = flatTasks.filter(t => t.done).map(t => t.title.toLowerCase());
    return zoneAlerts.filter(alert => {
      const alertWords = `${alert.title} ${alert.message || ''}`
        .toLowerCase().split(/\W+/).filter(w => w.length > 4);
      return !doneTitles.some(title => alertWords.some(word => title.includes(word)));
    });
  }, [zoneAlerts, flatTasks]);

  const incompleteTasks = flatTasks
    .filter(t => !t.done)
    .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9));
  const doneTasks       = flatTasks.filter(t => t.done);
  const topPriorityTask = incompleteTasks[0] || null;

  const recommendedProducts = useMemo(() => {
    if (!monthData?.sections) return [];
    return amazonAPI.getRecommendedProducts(monthData.sections, monthData.season, 5);
  }, [monthData]);

  // Month pill window: 5 months centred on active month
  const pillMonths = useMemo(() => {
    const idx   = monthIndex >= 0 ? monthIndex : new Date().getMonth();
    const start = Math.max(0, Math.min(idx - 2, 7)); // keep 5 visible
    return Array.from({ length: 5 }, (_, i) => ({
      short: MONTHS_SHORT[start + i],
      full:  MONTHS_FULL[start + i],
      idx:   start + i,
    }));
  }, [monthIndex]);

  const pct            = monthStats?.percentage || 0;
  const totalTasks     = monthStats?.total       || 0;
  const completedCount = monthStats?.completed   || 0;

  const handleCrossOff = (taskId) => {
    if (crossingOff.has(taskId)) return;
    setCrossingOff(prev => new Set([...prev, taskId]));
    setTimeout(() => {
      onTaskToggle(taskId);
      setCrossingOff(prev => { const s = new Set(prev); s.delete(taskId); return s; });
    }, 500);
  };

  const handleTabClick = (tab) => {
    // Single-container scroll: reset is synchronous and always reliable
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      lastScrollY.current = 0;
    }
    setActiveTab(tab);
    setNavVisible(true);
    if (tab === 'home') {
      setActiveView('home');
      setExpanded(false);
      if (onHomeClick) onHomeClick();
    }
    if (tab === 'calendar') setActiveView('calendar');
    if (tab === 'history')  setActiveView('history');
    if (tab === 'shop')     { if (onStoreClick)    onStoreClick(); }
    if (tab === 'settings') { if (onSettingsClick) onSettingsClick(); }
  };

  return (
    <div style={{
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: T.bg,
      height: '100dvh',
      overflow: 'hidden',
    }}>

      {/* ── Single scroll container (header + content together) ───────────── */}
      <div
        ref={scrollRef}
        style={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 80,
        }}
      >

        {/* ── Header — sticky green gradient ──────────────────────────────── */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'linear-gradient(160deg, #1a4a1c 0%, #1e5e20 100%)',
          padding: '8px 20px 20px',
        }}>
          {/* Brand row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img
                src="/logo192.png"
                alt="Lawn.Smart logo"
                style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontSize: 17, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                  <span style={{ fontWeight: 800, color: T.white }}>Lawn</span>
                  <span style={{ fontWeight: 800, color: T.teal }}>.Smart</span>
                </div>
              </div>
            </div>
            {/* Zone chip — frosted glass */}
            <div style={{
              background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 99, padding: '4px 12px',
              fontSize: 11, color: T.white, fontWeight: 600, whiteSpace: 'nowrap',
            }}>
              📍 Zone {settings.hardinessZone}
            </div>
          </div>

          {/* Greeting + month/year + location row */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>{greeting}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: T.white, letterSpacing: '-0.01em', marginTop: 2 }}>
                {monthName} {activeYear}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{settings.state}</div>
              {season && (
                <div style={{ fontSize: 11, color: T.teal, fontWeight: 600, marginTop: 2 }}>
                  {seasonEmoji} {season}
                </div>
              )}
            </div>
          </div>

          {/* Progress block — frosted glass container */}
          <div style={{
            background: 'rgba(255,255,255,0.12)', borderRadius: 14, padding: '12px 14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>Monthly Progress</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.white }}>{completedCount}/{totalTasks} tasks</span>
            </div>
            <div style={{ height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${pct}%`,
                background: T.teal, borderRadius: 99,
                transition: 'width 0.5s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
                {completedCount === 0 ? '0% complete' : `${pct}% complete`}
              </span>
              <span style={{ fontSize: 10, fontWeight: 600, color: T.teal }}>{pct}% done</span>
            </div>
          </div>
        </div>
        {/* ── End sticky header ────────────────────────────────────────────── */}

        {/* Calendar view */}
        {activeView === 'calendar' && (
          <CalendarView
            monthName={monthName}
            activeYear={activeYear}
            monthData={monthData}
            completedTasks={completedTasks}
            onTaskToggle={onTaskToggle}
            onMonthChange={onMonthChange}
            monthStats={monthStats}
            isDesktop={false}
          />
        )}

        {/* History view */}
        {activeView === 'history' && (
          <HistoryView
            completedTasks={completedTasks}
            zoneCustomizedData={zoneCustomizedData}
            activeYear={activeYear}
            isDesktop={false}
          />
        )}

        {/* Focus home view */}
        {activeView === 'home' && (
        <div style={{ padding: '16px 16px 0' }}>
        <>

        {/* Priority feature card — first high-priority incomplete task */}
        {topPriorityTask && (
          <div style={{
            background: T.white, borderRadius: 20, padding: 16, marginBottom: 14,
            boxShadow: `0 2px 12px ${T.shadow}`,
            borderLeft: '4px solid #c0392b',
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
              color: '#c0392b', textTransform: 'uppercase', marginBottom: 6,
            }}>
              ⚡ {topPriorityTask.priority === 'critical' ? 'Critical Task' : "This Month's Priority"}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.3, marginBottom: 4 }}>
              {topPriorityTask.title}
            </div>
            <div style={{ fontSize: 11, color: T.textLight, marginBottom: 14 }}>
              {topPriorityTask.cat}
            </div>
            <button
              onClick={() => handleCrossOff(topPriorityTask.id)}
              style={{
                background: T.green700, color: T.white, border: 'none',
                borderRadius: 10, padding: '8px 18px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Mark Complete ✓
            </button>
          </div>
        )}

        {/* Zone alert banner */}
        {activeAlerts.length > 0 && (
          <div style={{
            background: T.amberLight, border: `1px solid #f0c080`,
            borderRadius: 14, padding: '12px 14px', marginBottom: 14,
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>⚠️</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.amber }}>
                {activeAlerts[0].title || 'Zone Alert'}
              </div>
              <div style={{ fontSize: 11, color: '#8a5a1a', marginTop: 1 }}>
                {activeAlerts[0].message || ''}
              </div>
            </div>
            <button
              onClick={() => onDismissAlert && onDismissAlert(activeAlerts[0].title)}
              style={{
                background: 'none', border: 'none',
                cursor: 'pointer', color: '#c09060', fontSize: 14, flexShrink: 0,
              }}
            >✕</button>
          </div>
        )}

        {/* Month pill strip */}
        <div style={{
          display: 'flex', gap: 6, marginBottom: 14,
          overflowX: 'auto', paddingBottom: 4,
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}>
          {pillMonths.map(({ short, full }) => {
            const isActive = full === monthName;
            return (
              <div
                key={full}
                onClick={() => onMonthChange && onMonthChange(full)}
                style={{
                  flexShrink: 0, padding: '6px 14px', borderRadius: 99,
                  background: isActive ? T.green700 : T.white,
                  color:      isActive ? T.white    : T.textMed,
                  fontSize: 13, fontWeight: 600,
                  border: `1px solid ${isActive ? 'transparent' : T.border}`,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {short}
              </div>
            );
          })}
        </div>

        {/* All-done celebration card */}
        {incompleteTasks.length === 0 && (
          <div style={{
            background: T.green700, borderRadius: 20, padding: '20px 18px',
            marginBottom: 14, textAlign: 'center',
            boxShadow: `0 4px 16px rgba(30,94,32,0.25)`,
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.white, letterSpacing: '-0.01em' }}>
              All done for {monthName}!
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
              {totalTasks} tasks completed · Great work!
            </div>
          </div>
        )}

        {/* Up Next list */}
        <div style={{ marginBottom: 8 }}>
          {incompleteTasks.length > 0 && (
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>
            {`Up Next — ${incompleteTasks.length} remaining`}
          </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(expanded ? incompleteTasks : incompleteTasks.slice(0, 3)).map(task => {
              const crossing = crossingOff.has(task.id);
              return (
                <div
                  key={task.id}
                  style={{
                    background: T.white, borderRadius: 16,
                    padding: '13px 14px',
                    display: 'flex', alignItems: 'center', gap: 12,
                    boxShadow: `0 1px 4px ${T.shadow}`,
                    opacity: crossing ? 0.5 : 1,
                    transition: 'opacity 0.4s ease',
                  }}
                >
                  <Checkbox checked={crossing} onChange={() => handleCrossOff(task.id)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, lineHeight: 1.3,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      textDecoration: crossing ? 'line-through' : 'none',
                      transition: 'text-decoration 0s, color 0.3s ease',
                      color: crossing ? T.textLight : T.text,
                    }}>
                      {task.title}
                    </div>
                    <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{task.cat}</div>
                  </div>
                  {!crossing && <PriorityPill priority={task.priority} />}
                </div>
              );
            })}
          </div>

          {incompleteTasks.length > 3 && (
            <button
              onClick={() => setExpanded(e => !e)}
              style={{
                width: '100%', marginTop: 10, background: 'none',
                border: `1.5px dashed ${T.border}`,
                borderRadius: 14, padding: '10px',
                fontSize: 13, color: T.textMed, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {expanded ? 'Show less ↑' : `+ ${incompleteTasks.length - 3} more tasks`}
            </button>
          )}
        </div>

        {/* Product carousel */}
        <ProductCarousel
          products={recommendedProducts}
          monthName={monthName}
          onStoreClick={onStoreClick}
        />

        {/* Notes */}
        <NotesSection monthName={monthName} activeYear={activeYear} />

        {/* Completed section */}
        {doneTasks.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: T.textLight,
              marginBottom: 8, letterSpacing: '0.05em', textTransform: 'uppercase',
            }}>
              Completed
            </div>
            {doneTasks.map(task => (
              <div
                key={task.id}
                style={{
                  background: T.white, borderRadius: 14,
                  padding: '10px 14px',
                  display: 'flex', alignItems: 'center', gap: 12,
                  marginBottom: 6, opacity: 0.55,
                }}
              >
                <Checkbox checked={true} onChange={() => onTaskToggle(task.id)} />
                <div style={{
                  fontSize: 13, color: T.textLight,
                  textDecoration: 'line-through', flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {task.title}
                </div>
              </div>
            ))}
          </div>
        )}

        </>
        </div>
        )}

      </div>
      {/* ── End single scroll container ───────────────────────────────────── */}

      {/* ── Bottom navigation ───────────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: T.white, borderTop: `1px solid ${T.border}`,
        display: 'flex', padding: '8px 0',
        paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
        zIndex: 50,
        transform: navVisible ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.25s ease',
      }}>
        {[
          { id: 'home',     label: 'Home',     Icon: HomeIcon },
          { id: 'calendar', label: 'Calendar', Icon: CalendarIcon },
          { id: 'shop',     label: 'Shop',     Icon: ShopIcon },
          { id: 'settings', label: 'Settings', Icon: SettingsIcon },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => handleTabClick(id)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 3,
              background: 'none', border: 'none', cursor: 'pointer',
              color: activeTab === id ? T.green700 : T.textLight,
              padding: '4px 0',
            }}
          >
            <Icon />
            <span style={{ fontSize: 10, fontWeight: activeTab === id ? 700 : 400 }}>
              {label}
            </span>
          </button>
        ))}
      </div>

    </div>
  );
};

export default FocusMode;
